import { useEffect, useState } from 'react';
import { getFreelancerBanStatus, getFreelancerPunishments, getUnlockFee, unlockAccount } from '../../services/punishmentsService';
import { getEvaluationSummary } from '../../services/evaluationService';
import { api } from '../../config/api';
import './Accountstatussection.scss';

const REASON_LABELS = {
    NoShow: { label: 'Não comparecimento', icon: '🚫' },
    Disrespectful: { label: 'Comportamento desrespeitoso', icon: '😡' },
    SeriousIncident: { label: 'Incidente grave', icon: '⛔' },
};

function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric'
    });
}

function daysUntil(dateStr) {
    const diff = new Date(dateStr).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function AccountStatusModal({
    onClose,
    state,
    banStatus,
    punishments,
    evaluationSummary,
    attendanceRate,
    unlockFee,
    unlocking,
    unlockError,
    onUnlock,
    historyItems,
}) {
    const activePunishments = banStatus.activePunishments ?? 0;
    const isBanned = Boolean(banStatus.isBanned || activePunishments >= 3);

    return (
        <div className="acc-modal-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
            <div className="acc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="acc-modal__header">
                    <h3 className="acc-modal__title">🛡️ Status da Conta</h3>
                    <button className="acc-modal__close" onClick={onClose}>✕</button>
                </div>

                <div className="acc-modal__body">
                    {/* ── Estado principal ── */}
                    {state === 'green' && (
                        <div className="acc-state acc-state--green">
                            <div className="acc-state__header">
                                <span className="acc-state__dot" />
                                <span className="acc-state__label">Conta em perfeito estado</span>
                            </div>
                            <p className="acc-state__text">
                                <strong>Parabéns!</strong> Você nunca recebeu punições e possui uma boa reputação na plataforma.
                            </p>
                            <ul className="acc-state__checklist">
                                <li>✔ Pode se candidatar normalmente</li>
                                <li>✔ Conta verificada</li>
                                <li>✔ Sem restrições</li>
                            </ul>
                        </div>
                    )}

                    {state === 'yellow' && (
                        <div className="acc-state acc-state--yellow">
                            <div className="acc-state__header">
                                <span className="acc-state__dot" />
                                <span className="acc-state__label">Você possui advertências registradas</span>
                            </div>
                            <p className="acc-state__text">
                                Advertências: <strong>{activePunishments}/3</strong>
                            </p>
                            <div className="acc-state__reasons">
                                <span className="acc-state__reasons-title">Motivo(s):</span>
                                {punishments.slice(0, activePunishments).map((p) => {
                                    const r = REASON_LABELS[p.reason] ?? { label: p.reason, icon: '⚠️' };
                                    return <p key={p.id}>• {r.label}</p>;
                                })}
                            </div>
                            <p className="acc-state__hint">Evite novas infrações para não sofrer suspensão.</p>
                        </div>
                    )}

                    {state === 'red' && (
                        <div className="acc-state acc-state--red">
                            <div className="acc-state__header">
                                <span className="acc-state__dot" />
                                <span className="acc-state__label">Conta Suspensa</span>
                            </div>
                            <p className="acc-state__text">Sua conta está temporariamente suspensa.</p>
                            <div className="acc-state__ban-info">
                                <span>Motivo</span>
                                <strong>Acúmulo de 3 advertências</strong>
                            </div>
                            <div className="acc-state__ban-info">
                                <span>Fim da suspensão</span>
                                <strong>
                                    {new Date(banStatus.banExpiresAt).toLocaleString('pt-BR', {
                                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })}
                                    {' '}· Faltam {daysUntil(banStatus.banExpiresAt)} dia(s)
                                </strong>
                            </div>

                            <div className="acc-unlock">
                                <p className="acc-unlock__title">Desbloqueio imediato</p>
                                <p className="acc-unlock__desc">
                                    Libere sua conta agora pagando{' '}
                                    <strong>R$ {unlockFee.toFixed(2)}</strong>
                                </p>
                                {unlockError && <p className="acc-unlock__error">{unlockError}</p>}
                                <button
                                    className="acc-unlock__btn"
                                    onClick={onUnlock}
                                    disabled={unlocking || unlockFee <= 0}
                                >
                                    {unlocking ? 'Processando...' : `💳 Pagar desbloqueio`}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Nível de confiança ── */}
                    <div className="acc-trust">
                        <p className="acc-trust__title">Nível de confiança</p>
                        <div className="acc-trust__stars">
                            {'⭐'.repeat(Math.round(evaluationSummary?.average ?? 0)) || '—'}
                        </div>
                        {evaluationSummary?.badge && (
                            <p className="acc-trust__badge">{evaluationSummary.badge}</p>
                        )}
                        <div className="acc-trust__grid">
                            <div className="acc-trust__stat">
                                <span className="acc-trust__stat-label">Índice de comparecimento</span>
                                <span className="acc-trust__stat-value">
                                    {attendanceRate != null ? `${attendanceRate}%` : '—'}
                                </span>
                            </div>
                            <div className="acc-trust__stat">
                                <span className="acc-trust__stat-label">Avaliações</span>
                                <span className="acc-trust__stat-value">
                                    {evaluationSummary?.count > 0 ? evaluationSummary.average.toFixed(1) : '—'}
                                </span>
                            </div>
                            <div className="acc-trust__stat">
                                <span className="acc-trust__stat-label">Suspensões</span>
                                <span className="acc-trust__stat-value">{isBanned ? 1 : 0}</span>
                            </div>
                            <div className="acc-trust__stat">
                                <span className="acc-trust__stat-label">Advertências ativas</span>
                                <span className="acc-trust__stat-value">{activePunishments}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── Histórico ── */}
                    <div className="acc-history">
                        <p className="acc-history__title">Histórico da Conta</p>
                        {historyItems.map((item) => (
                            <div key={item.key} className={`acc-history__item acc-history__item--${item.variant}`}>
                                <span className="acc-history__icon">{item.icon}</span>
                                <div>
                                    <p className="acc-history__label">{item.label}</p>
                                    <p className="acc-history__meta">{item.meta}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AccountStatusSection({ freelancerId, accountCreatedAt, onNeedRecharge, buttonText = 'Status da conta', buttonClassName = '' }) {
    const [banStatus, setBanStatus] = useState({ isBanned: false, activePunishments: 0 });
    const [punishments, setPunishments] = useState([]);
    const [evaluationSummary, setEvaluationSummary] = useState(null);
    const [attendanceRate, setAttendanceRate] = useState(null);
    const [unlockFee, setUnlockFee] = useState(0);
    const [loading, setLoading] = useState(true);
    const [unlocking, setUnlocking] = useState(false);
    const [unlockError, setUnlockError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            if (!freelancerId) {
                setLoading(false);
                return;
            }

            try {
                const [statusResult, historyResult, evalSummaryResult, attendanceResult, feeResult] = await Promise.allSettled([
                    getFreelancerBanStatus(),
                    getFreelancerPunishments(),
                    getEvaluationSummary(freelancerId),
                    api.get(`/applications/freelancer/${freelancerId}/attendance-stats`).then(r => r.data),
                    getUnlockFee(),
                ]);

                const status = statusResult.status === 'fulfilled' ? statusResult.value : { isBanned: false, activePunishments: 0 };
                const history = historyResult.status === 'fulfilled' ? historyResult.value : [];
                const evalSummary = evalSummaryResult.status === 'fulfilled' ? evalSummaryResult.value : null;
                const attendance = attendanceResult.status === 'fulfilled' ? attendanceResult.value : null;
                const fee = feeResult.status === 'fulfilled' ? feeResult.value : { fee: 0 };

                setBanStatus(status);
                setPunishments(history);
                setEvaluationSummary(evalSummary);
                setAttendanceRate(attendance?.attendanceRate ?? null);
                setUnlockFee(fee?.fee ?? 0);

                const hadFailures = [statusResult, historyResult, evalSummaryResult, attendanceResult, feeResult]
                    .some((result) => result.status === 'rejected');

            } catch (err) {
                console.error(err);
                setLoadError('Não foi possível carregar o status da conta agora.');
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [freelancerId]);

    const handleUnlock = async () => {
        setUnlocking(true);
        setUnlockError(null);
        try {
            await unlockAccount();
            const [status, history] = await Promise.all([
                getFreelancerBanStatus(),
                getFreelancerPunishments(),
            ]);
            setBanStatus(status);
            setPunishments(history);
        } catch (err) {
            const data = err.response?.data;
            if (data?.error === 'insufficient_balance') {
                setUnlockError(data.message);
                onNeedRecharge?.();
            } else {
                setUnlockError(data?.message || 'Erro ao processar o desbloqueio.');
            }
        } finally {
            setUnlocking(false);
        }
    };

    if (loading || !banStatus) return null;

    const activePunishments = banStatus.activePunishments ?? 0;
    const isBanned = Boolean(banStatus.isBanned || activePunishments >= 3);

    let state = 'green';
    if (isBanned) state = 'red';
    else if (activePunishments > 0) state = 'yellow';

    const linkText = {
        green: 'Status da conta — Em dia',
        yellow: `Status da conta — ${activePunishments}/3 advertências`,
        red: 'Status da conta — Suspensa',
    }[state];

    const linkVariant = {
        green: 'acc-status-link--ok',
        yellow: 'acc-status-link--warning',
        red: 'acc-status-link--banned',
    }[state];

    const linkIcon = { green: '🟢', yellow: '⚠️', red: '⛔' }[state];

    // Monta o histórico combinando: criação da conta + punições + suspensão atual
    const historyItems = [];
    if (accountCreatedAt) {
        historyItems.push({
            key: 'created',
            icon: '✓',
            label: 'Conta criada',
            meta: formatDate(accountCreatedAt),
            variant: 'positive',
        });
    }
    punishments.forEach((p) => {
        const r = REASON_LABELS[p.reason] ?? { label: p.reason, icon: '⚠️' };
        historyItems.push({
            key: p.id,
            icon: '⚠',
            label: 'Advertência',
            meta: `${r.label} · ${formatDate(p.createdAt)}`,
            variant: 'warning',
        });
    });
    if (isBanned) {
        historyItems.push({
            key: 'ban',
            icon: '🔒',
            label: 'Conta suspensa',
            meta: '7 dias',
            variant: 'danger',
        });
    }

    return (
        <>
            <button
                type="button"
                className={`acc-status-link ${linkVariant} ${buttonClassName}`.trim()}
                onClick={() => setShowModal(true)}
            >
                <span className="acc-status-link__icon">{linkIcon}</span>
                <span className="acc-status-link__text">{buttonText}</span>
                <span className="acc-status-link__arrow">›</span>
            </button>

            {showModal && (
                <AccountStatusModal
                    onClose={() => setShowModal(false)}
                    state={state}
                    banStatus={banStatus}
                    punishments={punishments}
                    evaluationSummary={evaluationSummary}
                    attendanceRate={attendanceRate}
                    unlockFee={unlockFee}
                    unlocking={unlocking}
                    unlockError={unlockError}
                    onUnlock={handleUnlock}
                    historyItems={historyItems}
                />
            )}

            {loadError && (
                <p className="acc-status-link__hint">{loadError}</p>
            )}
        </>
    );
}